import TextField from '@mui/material/TextField';
import { useState, useEffect, useMemo } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuItem from '@mui/material/MenuItem';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import { REMOVE_PRODUCT_DETAILS, UPDATE_PRODUCT_RESET } from '../../constants/productConstants';
import { clearErrors, getProductDetails, updateProduct } from '../../actions/productAction';
import { getAllBrands, createBrand, updateBrand } from '../../actions/brandAction';
import { getCategories } from '../../actions/categoryAction';
import ImageIcon from '@mui/icons-material/Image';
import BackdropLoader from '../Layouts/BackdropLoader';
import MetaData from '../Layouts/MetaData';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import Autocomplete from '@mui/material/Autocomplete';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, IconButton } from '@mui/material';
import { CREATE_BRAND_RESET, UPDATE_BRAND_RESET } from '../../constants/brandConstants';
import EditIcon from '@mui/icons-material/Edit';

const flattenCategories = (categories) => {
    const flatCategories = [];
    const traverse = (category, level) => {
        flatCategories.push({
            ...category,
            name: `${'—'.repeat(level)} ${category.name}`,
        });
        if (category.children) {
            category.children.forEach(child => traverse(child, level + 1));
        }
    };
    categories.forEach(category => traverse(category, 0));
    return flatCategories;
};

const UpdateProduct = () => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const params = useParams();
    const theme = useTheme();

    const { loading, product, error } = useSelector((state) => state.productDetails);
    const { loading: updateLoading, isUpdated, error: updateError } = useSelector((state) => state.product);
    const { brands } = useSelector((state) => state.brands);
    const { categories } = useSelector((state) => state.categories);
    const { loading: brandLoading, success: brandSuccess, error: brandError, brand: newBrand } = useSelector((state) => state.newBrand);
    const { loading: updateBrandLoading, isUpdated: isBrandUpdated, error: updateBrandError } = useSelector((state) => state.brandDetails);

    const [highlights, setHighlights] = useState([]);
    const [highlightInput, setHighlightInput] = useState("");
    const [specs, setSpecs] = useState([]);
    const [specsInput, setSpecsInput] = useState({
        title: "",
        description: ""
    });

    const [sizes, setSizes] = useState([]);
    const [sizeInput, setSizeInput] = useState("");

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [price, setPrice] = useState(0);
    const [mrp, setMrp] = useState(0);
    const [deliveryCharges, setDeliveryCharges] = useState(0);
    const [category, setCategory] = useState(null);
    const [stock, setStock] = useState(0);
    const [warranty, setWarranty] = useState(0);
    const [brand, setBrand] = useState(null);
    const [images, setImages] = useState([]);
    const [oldImages, setOldImages] = useState([]);
    const [imagesPreview, setImagesPreview] = useState([]);

    const [open, setOpen] = useState(false);
    const [newBrandName, setNewBrandName] = useState("");
    const [newBrandLogo, setNewBrandLogo] = useState("");
    const [newBrandLogoPreview, setNewBrandLogoPreview] = useState("");
    const [editingBrand, setEditingBrand] = useState(false);

    const [editedHighlight, setEditedHighlight] = useState(-1);
    const [editedSpec, setEditedSpec] = useState(-1);

    const handleClose = () => {
        setOpen(false);
    };

    const handleNewBrandLogoChange = (e) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (reader.readyState === 2) {
                setNewBrandLogoPreview(reader.result);
                setNewBrandLogo(reader.result);
            }
        };

        reader.readAsDataURL(e.target.files[0]);
    }

    const handleCreateBrand = () => {
        if (!newBrandName.trim() || !newBrandLogo) {
            enqueueSnackbar("Brand Name and Logo are required", { variant: "warning" });
            return;
        }
        dispatch(createBrand({ name: newBrandName, logo: newBrandLogo }));
    }

    const handleUpdateBrand = () => {
        if (!newBrandName.trim()) {
            enqueueSnackbar("Brand Name is required", { variant: "warning" });
            return;
        }
        const brandData = {
            name: newBrandName,
        }
        if (newBrandLogo) {
            brandData.logo = newBrandLogo
        }
        dispatch(updateBrand(brand._id, brandData));
    }

    const handleSpecsChange = (e) => {
        setSpecsInput({ ...specsInput, [e.target.name]: e.target.value });
    }

    const addSpecs = () => {
        if (!specsInput.title.trim() || !specsInput.description.trim()) return;
        if (editedSpec !== -1) {
            const updatedSpecs = [...specs];
            updatedSpecs[editedSpec] = specsInput;
            setSpecs(updatedSpecs);
            setEditedSpec(-1);
        } else {
            setSpecs([...specs, specsInput]);
        }
        setSpecsInput({ title: "", description: "" });
    }

    const editSpec = (index) => {
        setEditedSpec(index);
        setSpecsInput(specs[index]);
    }

    const addHighlight = () => {
        if (!highlightInput.trim()) return;
        if (editedHighlight !== -1) {
            const updatedHighlights = [...highlights];
            updatedHighlights[editedHighlight] = highlightInput;
            setHighlights(updatedHighlights);
            setEditedHighlight(-1);
        } else {
            setHighlights([...highlights, highlightInput]);
        }
        setHighlightInput("");
    }

    const editHighlight = (index) => {
        setEditedHighlight(index);
        setHighlightInput(highlights[index]);
    }

    const deleteHighlight = (index) => {
        setHighlights(highlights.filter((h, i) => i !== index))
    }

    const deleteSpec = (index) => {
        setSpecs(specs.filter((s, i) => i !== index))
    }

    const addSize = () => {
        if (!sizeInput.trim()) return;
        setSizes([...sizes, sizeInput]);
        setSizeInput("");
    }

    const deleteSize = (index) => {
        setSizes(sizes.filter((s, i) => i !== index));
    }

    const deleteOldImage = (index) => {
        setOldImages(oldImages.filter((img, i) => i !== index));
    };

    const deleteImage = (index) => {
        setImages(images.filter((_, i) => i !== index));
        setImagesPreview(imagesPreview.filter((_, i) => i !== index));
    };

    const handleProductImageChange = (e) => {
        const newFiles = Array.from(e.target.files);

        const fileReadPromises = newFiles.map(file => {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = () => {
                    if (reader.readyState === 2) {
                        resolve(reader.result);
                    }
                };
                reader.readAsDataURL(file);
            });
        });

        Promise.all(fileReadPromises).then(results => {
            setImages((oldImages) => [...oldImages, ...results]);
            setImagesPreview((oldImagesPreview) => [...oldImagesPreview, ...results]);
        });
    };

    const newProductSubmitHandler = (e) => {
        e.preventDefault();

        if (!brand) {
            enqueueSnackbar("Select Brand", { variant: "warning" });
            return;
        }
        if (!category) {
            enqueueSnackbar("Select Category", { variant: "warning" });
            return;
        }

        const formData = new FormData();

        formData.set("name", name);
        formData.set("description", description);
        formData.set("price", price);
        formData.set("mrp", mrp);
        formData.set("deliveryCharges", deliveryCharges);
        formData.set("category", category._id);
        formData.set("stock", stock);
        formData.set("warranty", warranty);
        formData.set("brand", brand._id);

        images.forEach((image) => {
            formData.append("images", image);
        });

        (highlights || []).forEach((h) => {
            formData.append("highlights", h);
        });

        (sizes || []).forEach((s) => {
            formData.append("sizes", s);
        });

        specs.forEach((s) => {
            formData.append("specifications", JSON.stringify(s));
        });

        dispatch(updateProduct(params.id, formData));
    }

    const productId = params.id;

    const flattenedCategories = useMemo(() => (categories ? flattenCategories(categories) : []), [categories]);

    useEffect(() => {
        if (product && product._id !== productId) {
            dispatch(getProductDetails(productId));
        } else if (product) {
            setName(product.name);
            setDescription(product.description);
            setPrice(product.price);
            setMrp(product.mrp);
            setDeliveryCharges(product.deliveryCharges);
            setStock(product.stock);
            setWarranty(product.warranty);
            setHighlights(product.highlights);
            setSpecs(product.specifications);
            setSizes(product.sizes);
            setOldImages(product.images);
        }
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        if (updateError) {
            enqueueSnackbar(updateError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isUpdated) {
            enqueueSnackbar("Product Updated Successfully", { variant: "success" });
            dispatch({ type: UPDATE_PRODUCT_RESET });
            dispatch({ type: REMOVE_PRODUCT_DETAILS });
            navigate('/admin/products');
        }
    }, [dispatch, error, isUpdated, navigate, product, productId, updateError, enqueueSnackbar]);

    useEffect(() => {
        if (product && product.category && flattenedCategories.length > 0) {
            const categoryObj = flattenedCategories.find(c => c._id === product.category._id);
            if (categoryObj) {
                setCategory(categoryObj);
            }
        }
    }, [product, flattenedCategories]);

    useEffect(() => {
        if (product && product.brand && brands.length > 0) {
            const brandObj = brands.find(b => b._id === product.brand._id);
            if (brandObj) {
                setBrand(brandObj);
            }
        }
    }, [product, brands]);

    useEffect(() => {
        dispatch(getAllBrands());
        dispatch(getCategories());
        if (brandError) {
            enqueueSnackbar(brandError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (brandSuccess) {
            enqueueSnackbar("Brand Created", { variant: "success" });
            setOpen(false);
            dispatch({ type: CREATE_BRAND_RESET });
            dispatch(getAllBrands());
            setBrand(newBrand);
        }
        if (updateBrandError) {
            enqueueSnackbar(updateBrandError, { variant: "error" });
            dispatch(clearErrors());
        }
        if (isBrandUpdated) {
            enqueueSnackbar("Brand Updated", { variant: "success" });
            setOpen(false);
            dispatch({ type: UPDATE_BRAND_RESET });
            dispatch(getAllBrands());
            setBrand(null);
        }
    }, [dispatch, brandError, brandSuccess, newBrand, isBrandUpdated, updateBrandError, enqueueSnackbar]);

    return (
        <>
            <MetaData title="Admin: Update Product | Flipkart" />

            {loading && <BackdropLoader />}
            {updateLoading && <BackdropLoader />}
            <form onSubmit={newProductSubmitHandler} encType="multipart/form-data" className="p-4 sm:p-6">
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-8"
                >
                    <h2 className="text-xl font-bold mb-6 text-center text-gray-800 dark:text-white">General</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <TextField label="Name" variant="outlined" fullWidth required value={name} onChange={(e) => setName(e.target.value)} />
                        <TextField label="Description" multiline rows={3} variant="outlined" fullWidth required value={description} onChange={(e) => setDescription(e.target.value)} />
                        <TextField label="Price" type="number" variant="outlined" fullWidth required value={price} onChange={(e) => setPrice(e.target.value)} />
                        <TextField label="M.R.P" type="number" variant="outlined" fullWidth required value={mrp} onChange={(e) => setMrp(e.target.value)} />
                        <TextField label="Delivery Charges" type="number" variant="outlined" fullWidth required value={deliveryCharges} onChange={(e) => setDeliveryCharges(e.target.value)} />
                        <div className="flex justify-between gap-4">
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={flattenedCategories}
                            getOptionLabel={(option) => option.name || ""}
                            isOptionEqualToValue={(option, value) => option._id === value?._id}
                            value={category}
                            onChange={(event, newValue) => {
                                setCategory(newValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Category"
                                    variant="outlined"
                                    required
                                />
                            )}
                        />
                        </div>
                        <TextField label="Stock" type="number" variant="outlined" fullWidth required value={stock} onChange={(e) => setStock(e.target.value)} />
                        <TextField label="Warranty" type="number" variant="outlined" fullWidth required value={warranty} onChange={(e) => setWarranty(e.target.value)} />
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-8 mt-6"
                >
                    <h2 className="text-xl font-bold mb-6 text-center text-gray-800 dark:text-white">Highlights</h2>
                    <div className="flex gap-4">
                        <TextField label="Highlight" variant="outlined" fullWidth value={highlightInput} onChange={(e) => setHighlightInput(e.target.value)} />
                        <Button variant="contained" onClick={addHighlight}>{editedHighlight !== -1 ? "Update" : "Add"}</Button>
                    </div>
                    <div className="mt-4 space-y-2">
                        {(highlights || []).map((h, i) => (
                            <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
                                <p className="font-medium">{h}</p>
                                <div>
                                    <IconButton onClick={() => editHighlight(i)} color="primary">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => deleteHighlight(i)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-8 mt-6"
                >
                    <h2 className="text-xl font-bold mb-6 text-center text-gray-800 dark:text-white">Brand</h2>
                    <div className="flex items-center gap-6">
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={brands ? [{ name: "Add New Brand", isAddNew: true }, ...brands] : [{ name: "Add New Brand", isAddNew: true }]}
                            getOptionLabel={(option) => option.name || ""}
                            isOptionEqualToValue={(option, value) => option._id === value?._id}
                            value={brand}
                            onChange={(event, newValue) => {
                                if (newValue && newValue.isAddNew) {
                                    setEditingBrand(false);
                                    setOpen(true);
                                } else {
                                    setBrand(newValue);
                                }
                            }}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    {option.isAddNew ? (
                                        <span style={{ fontStyle: 'italic' }}>{option.name}</span>
                                    ) : (
                                        <div className="flex items-center">
                                            {option.logo && <img src={option.logo.url} alt={option.name} style={{ width: 30, height: 30, marginRight: 10, objectFit: 'contain' }} />}
                                            {option.name}
                                        </div>
                                    )}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Brand"
                                    variant="outlined"
                                    required
                                />
                            )}
                        />
                        <IconButton onClick={() => {
                            if (brand) {
                                setEditingBrand(true);
                                setNewBrandName(brand.name);
                                setNewBrandLogoPreview(brand.logo.url);
                                setOpen(true);
                            }
                        }} disabled={!brand}>
                            <EditIcon />
                        </IconButton>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-8 mt-6"
                >
                    <h2 className="text-xl font-bold mb-6 text-center text-gray-800 dark:text-white">Sizes</h2>
                    <div className="flex gap-4">
                        <TextField label="Size" variant="outlined" fullWidth value={sizeInput} onChange={(e) => setSizeInput(e.target.value)} />
                        <Button variant="contained" onClick={addSize}>Add</Button>
                    </div>
                    <div className="mt-4 space-y-2">
                        {(sizes || []).map((s, i) => (
                            <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
                                <p className="font-medium">{s}</p>
                                <div>
                                    <IconButton onClick={() => deleteSize(i)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.3 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-8 mt-6"
                >
                    <h2 className="text-xl font-bold mb-6 text-center text-gray-800 dark:text-white">Specifications</h2>
                    <div className="flex gap-4 mb-4">
                        <TextField label="Title" name="title" variant="outlined" fullWidth value={specsInput.title} onChange={handleSpecsChange} />
                        <TextField label="Description" name="description" variant="outlined" fullWidth value={specsInput.description} onChange={handleSpecsChange} />
                        <Button variant="contained" onClick={addSpecs}>{editedSpec !== -1 ? "Update" : "Add"}</Button>
                    </div>
                    <div className="space-y-2">
                        {specs.map((spec, i) => (
                            <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
                                <div>
                                    <p className="font-semibold">{spec.title}</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{spec.description}</p>
                                </div>
                                <div>
                                    <IconButton onClick={() => editSpec(i)} color="primary">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => deleteSpec(i)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.4 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 sm:p-8 mt-6"
                >
                    <h2 className="text-xl font-bold mb-6 text-center text-gray-800 dark:text-white">Product Images</h2>
                    <div className="flex flex-col gap-2">
                        <div className="flex gap-2 overflow-x-auto pb-2">
                            {(oldImages || []).map((image, i) => (
                                <div key={i} className="relative w-32 h-32 flex-shrink-0">
                                    <img draggable="false" src={image.lowUrl} alt="Product" className="w-full h-full object-cover rounded-lg" />
                                    <IconButton onClick={() => deleteOldImage(i)} color="error" size="small" sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </div>
                            ))}
                            {(imagesPreview || []).map((image, i) => (
                                <div key={i} className="relative w-32 h-32 flex-shrink-0">
                                    <img draggable="false" src={image} alt="Product" className="w-full h-full object-cover rounded-lg" />
                                    <IconButton onClick={() => deleteImage(i)} color="error" size="small" sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </div>
                            ))}
                        </div>
                        <TextField
                            type="file"
                            label="Upload Images"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                multiple: true,
                                accept: "image/*",
                            }}
                            onChange={handleProductImageChange}
                            variant="outlined"
                            fullWidth
                        />
                    </div>
                </motion.div>

                <div className="flex justify-center mt-8">
                    <Button type="submit" variant="contained" color="success" sx={{ width: { xs: '100%', sm: '33.33%' }, py: 1.5 }}>
                        Update Product
                    </Button>
                </div>
            </form>

            <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: "1.25rem" } }}>
                <DialogTitle sx={{ pb: 1 }}>{editingBrand ? "Edit Brand" : "Add New Brand"}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 400 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Brand Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={newBrandName}
                        onChange={(e) => setNewBrandName(e.target.value)}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 100, height: 100, border: '2px dashed grey', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {newBrandLogoPreview ? 
                                <img draggable="false" src={newBrandLogoPreview} alt="Brand Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : 
                                <ImageIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                            }
                        </Box>
                        <TextField
                            type="file"
                            label="Upload Logo"
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                accept: "image/*",
                            }}
                            onChange={handleNewBrandLogoChange}
                            variant="outlined"
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={editingBrand ? handleUpdateBrand : handleCreateBrand} disabled={brandLoading || updateBrandLoading} variant="contained">{editingBrand ? "Update" : "Add"}</Button>
                </DialogActions>
            </Dialog>

        </>
    );
};

export default UpdateProduct;